import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { authStore, logout as doLogout, updateMyProfile } from '@/lib/auth.js'
import { API_BASE_URL } from '@/config/env.js'
import { apiFetch } from '@/lib/auth.js'

const AuthContext = createContext(null)

const hasUserProfileIdentity = (value) => Boolean(
  value
    && (
      value.id != null
      || value.pk != null
      || value.profile_uuid
      || value.email
      || value.username
      || value.display_name
    ),
)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)           // null = loading, false = guest
  const [entitlements, setEntitlements] = useState(null)
  const syncTimersRef = useRef([])
  const mountedRef = useRef(false)

  const clearSyncTimers = useCallback(() => {
    syncTimersRef.current.forEach(clearTimeout)
    syncTimersRef.current = []
  }, [])

  useEffect(() => {
    mountedRef.current = true
    const unsubscribe = authStore.subscribe(() => {
      clearSyncTimers()
      setUser(authStore.getAccess() || authStore.getRefresh() ? null : false)
      setEntitlements(null)
    })
    return () => {
      mountedRef.current = false
      clearSyncTimers()
      unsubscribe()
    }
  }, [clearSyncTimers])

  const fetchEntitlements = useCallback(async () => {
    const session = authStore.getSession()
    try {
      const res = await apiFetch(`${API_BASE_URL}/accounts/me/entitlements/`)
      if (res.ok) {
        const data = await res.json()
        if (mountedRef.current && authStore.isCurrentSession(session)) setEntitlements(data)
      }
    } catch {
      /* Entitlement refresh is best-effort; auth state stays usable without it. */
    }
  }, [])

  // Re-sync entitlements after a checkout. The Stripe webhook that activates the
  // subscription can lag behind the success redirect, and the success modal may
  // be dismissed before it lands — so the retries live here (provider stays
  // mounted for the whole session) instead of in the ephemeral success page.
  const syncEntitlementsAfterCheckout = useCallback(() => {
    const session = authStore.getSession()
    clearSyncTimers()
    fetchEntitlements()
    syncTimersRef.current = [1500, 4000, 8000, 15000].map((ms) =>
      setTimeout(() => {
        if (authStore.isCurrentSession(session)) fetchEntitlements()
      }, ms),
    )
  }, [clearSyncTimers, fetchEntitlements])

  const fetchMe = useCallback(async ({ clearOnFailure = true } = {}) => {
    const session = authStore.getSession()
    if (!authStore.getAccess() && !authStore.getRefresh()) {
      if (clearOnFailure) setUser(false)
      return null
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/accounts/me/`)
      if (res.ok) {
        const data = await res.json()
        if (!mountedRef.current || !authStore.isCurrentSession(session)) return null
        setUser(data)
        fetchEntitlements()
        return data
      } else {
        if (clearOnFailure && mountedRef.current && authStore.isCurrentSession(session)) setUser(false)
      }
    } catch {
      if (clearOnFailure && mountedRef.current && authStore.isCurrentSession(session)) setUser(false)
    }
    return null
  }, [fetchEntitlements])

  useEffect(() => { fetchMe() }, [fetchMe])

  const loginSuccess = useCallback((userData) => {
    clearSyncTimers()
    setEntitlements(null)
    const nextUser = userData?.user || userData?.profile || userData

    if (hasUserProfileIdentity(nextUser)) {
      setUser(nextUser)
      fetchEntitlements()
      fetchMe({ clearOnFailure: false })
      return
    }

    setUser(null)
    fetchMe()
  }, [clearSyncTimers, fetchEntitlements, fetchMe])

  const logout = useCallback(() => {
    doLogout()
    setUser(false)
    setEntitlements(null)
  }, [])

  const refetchMe = useCallback(() => fetchMe({ clearOnFailure: false }), [fetchMe])
  const saveMyProfile = useCallback(async (patch) => {
    const session = authStore.getSession()
    const data = await updateMyProfile(patch)
    if (mountedRef.current && authStore.isCurrentSession(session) && hasUserProfileIdentity(data)) {
      setUser(data)
    }
    return data
  }, [])

  return (
    <AuthContext.Provider value={{ user, entitlements, loginSuccess, logout, refetchEntitlements: fetchEntitlements, refetchMe, saveMyProfile, syncEntitlementsAfterCheckout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
